import { useState, useEffect, useRef } from 'react'

export interface SiteSettings {
  site_name: string
  site_description: string
  auth_logo?: string
  sidenav_logo?: string
}

const defaultSettings: SiteSettings = {
  site_name: import.meta.env.VITE_APP_NAME || 'ADMIN PANEL',
  site_description: '',
  auth_logo: '/assets/img/als-logo-2.png',
  sidenav_logo: '/assets/img/als-white-logo.png',
}

let cachedSettings: SiteSettings | null = null
let settingsPromise: Promise<SiteSettings> | null = null
let isLoading = false
let hasAttemptedLoad = false

/**
 * Hook to load and use site settings from the database
 * Settings are cached to avoid multiple API calls
 */
export function useSiteSettings() {
  const [settings, setSettings] = useState<SiteSettings>(() => cachedSettings || defaultSettings)
  const [loading, setLoading] = useState(false)
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (hasInitialized.current) {
      return
    }
    hasInitialized.current = true

    if (cachedSettings) {
      const settingsToUse = cachedSettings
      setSettings((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(settingsToUse)) {
          return prev
        }
        return settingsToUse
      })
      setLoading(false)
      return
    }

    if (hasAttemptedLoad && !cachedSettings) {
      setLoading(false)
      return
    }

    if (isLoading || settingsPromise) {
      if (settingsPromise) {
        settingsPromise
          .then((loadedSettings) => {
            setSettings(loadedSettings)
            setLoading(false)
          })
          .catch(() => {
            hasAttemptedLoad = true
            setLoading(false)
          })
      }
      return
    }

    hasAttemptedLoad = true
    isLoading = false
    setLoading(false)
    cachedSettings = defaultSettings
    setSettings(defaultSettings)
  }, [])

  const refresh = () => {
    cachedSettings = defaultSettings
    settingsPromise = null
    hasAttemptedLoad = true
    isLoading = false
    setLoading(false)
    setSettings(defaultSettings)
  }

  return { settings, loading, refresh }
}

